import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import API_BASE from '../config';
import {
  Banner, Loading, Empty, StatusBadge, FlowBadge, SectionIntro,
  formatDate, formatDateTime, errorMessage,
} from '../components/ui';
import './Dashboard.css';

// Categorical series colours, drawn from the semantic palette.
const SERIES = {
  newIssues: '#0E7C7B',
  renewals: '#3730A3',
  denied: '#DC2626',
};

const PIE_COLOURS = ['#0E7C7B', '#3730A3', '#D97706', '#16A34A', '#0A5C5B', '#6B7280'];

// Handed to the detail page so its back button returns here rather than to the
// full application list.
const BACK_TO_DASHBOARD = { to: '/dashboard', label: 'Dashboard' };

function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/dashboard`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, 'Could not load the dashboard.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return <div className="page-container"><div className="page-card"><Loading /></div></div>;
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-card"><Banner kind="error" message={error} /></div>
      </div>
    );
  }

  const c = data.counts || {};
  const monthly = (data.monthly || []).map((m) => ({
    month: m.Month,
    'New issues': m.NewIssues || 0,
    Renewals: m.Renewals || 0,
    Denied: m.Denied || 0,
  }));

  const commodityPie = (data.byCommodity || [])
    .filter((x) => x.Applications > 0)
    .map((x) => ({ name: x.Name, value: x.Applications }));

  const kpis = [
    { label: 'Awaiting review', value: c.PendingReview || 0, tone: (c.PendingReview || 0) > 0 ? 'is-amber' : 'is-muted', status: 'Pending Review' },
    { label: 'Decision made', value: c.Reviewed || 0, tone: 'is-muted', status: 'Reviewed' },
    { label: 'Ready to send', value: c.ReadyToSend || 0, tone: (c.ReadyToSend || 0) > 0 ? 'is-amber' : 'is-muted', status: 'Ready to Send' },
    { label: 'Sent', value: c.Sent || 0, tone: 'is-green', status: 'Sent' },
    { label: 'Send failed', value: c.SendFailed || 0, tone: (c.SendFailed || 0) > 0 ? 'is-red' : 'is-muted', status: 'Send Failed' },
    { label: 'Total applications', value: c.Total || 0, tone: '' },
  ];

  return (
    <div className="page-container">
      <div className="page-card">
        <div className="page-header">
          <div>
            <h2>Dashboard</h2>
            <p className="page-subtitle">
              Where every Certificate of Good Standing application currently sits.
            </p>
          </div>
        </div>

        <div className="kpi-ribbon">
          {kpis.map((k) => (
            <div
              key={k.label}
              className={`kpi-card${k.status ? ' is-clickable' : ''}`}
              onClick={k.status ? () => navigate(`/applications?status=${encodeURIComponent(k.status)}`) : undefined}
              role={k.status ? 'button' : undefined}
              tabIndex={k.status ? 0 : undefined}
              onKeyDown={k.status ? (e) => { if (e.key === 'Enter') navigate(`/applications?status=${encodeURIComponent(k.status)}`); } : undefined}
            >
              <div className="kpi-label">{k.label}</div>
              <div className={`kpi-value ${k.tone}`}>{k.value}</div>
            </div>
          ))}
        </div>

        <div className="dash-split">
          <div className="dash-chart-card">
            <h3 className="dash-chart-title">Applications by month</h3>
            {monthly.length === 0 ? (
              <Empty title="No applications yet." hint="The chart fills in as applications arrive." />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={monthly} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="New issues" stackId="a" fill={SERIES.newIssues} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Renewals" stackId="a" fill={SERIES.renewals} />
                  <Bar dataKey="Denied" stackId="a" fill={SERIES.denied} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="dash-chart-card">
            <h3 className="dash-chart-title">Commodities applied for</h3>
            {commodityPie.length === 0 ? (
              <Empty title="Nothing to chart yet." hint="Commodity mix appears once applications arrive." />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={commodityPie}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={88}
                    innerRadius={48}
                    paddingAngle={2}
                  >
                    {commodityPie.map((entry, i) => (
                      <Cell key={entry.name} fill={PIE_COLOURS[i % PIE_COLOURS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="dash-split">
        <div className="page-card">
          <h3 className="dash-chart-title">Latest applications</h3>
          {(data.recent || []).length === 0 ? (
            <Empty title="No applications yet." hint="New Google Form entries appear here automatically." />
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Applicant</th>
                    <th className="nowrap">Status</th>
                    <th className="nowrap">Outcome</th>
                    <th className="nowrap">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent.map((r) => (
                    <tr key={r.Id} className="row-clickable" onClick={() => navigate(`/applications/${r.Id}`, { state: { from: BACK_TO_DASHBOARD } })}>
                      <td>{r.CompanyName || 'Unnamed applicant'}</td>
                      <td className="nowrap"><StatusBadge status={r.Status} /></td>
                      <td className="nowrap"><FlowBadge flowType={r.FlowType} /></td>
                      <td className="nowrap">{formatDate(r.CreatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="page-card">
          <h3 className="dash-chart-title">Certificates expiring within 60 days</h3>
          <SectionIntro>
            These facilities will need a renewal soon. A new form submission from them routes to
            the renewal flow automatically.
          </SectionIntro>
          {(data.expiring || []).length === 0 ? (
            <Empty title="Nothing expiring in the next 60 days." />
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Facility</th>
                    <th className="nowrap">Account code</th>
                    <th className="nowrap">Valid until</th>
                  </tr>
                </thead>
                <tbody>
                  {data.expiring.map((e) => (
                    <tr key={e.Id}>
                      <td>{e.CompanyName}</td>
                      <td className="nowrap">{e.InternalAccountCode}</td>
                      <td className="nowrap">{formatDate(e.ValidUntil)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {(data.recentFailures || []).length > 0 && (
        <div className="page-card">
          <h3 className="dash-chart-title dash-title-red">Recent email failures</h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Applicant</th>
                  <th>Recipient</th>
                  <th>Error</th>
                  <th className="nowrap">When</th>
                </tr>
              </thead>
              <tbody>
                {data.recentFailures.map((f) => (
                  <tr
                    key={f.Id}
                    className={f.ApplicationId ? 'row-clickable' : ''}
                    onClick={f.ApplicationId
                      ? () => navigate(`/applications/${f.ApplicationId}`, { state: { from: BACK_TO_DASHBOARD } })
                      : undefined}
                  >
                    <td>{f.CompanyName || '—'}</td>
                    <td>{f.ToEmail}</td>
                    <td className="dash-error-cell">{f.ErrorMessage}</td>
                    <td className="nowrap">{formatDateTime(f.SentAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
